import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, getToken } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { urlImpression } from '../api/config';
import { Signature } from '../components/SchemaVoiture';
import { centimes, dateFr, montant, nomClient } from '../lib/format';
import { T } from '../i18n';

interface Caution {
  _id: string; montantMinor: number; devise: string; forme: string; numeroPiece?: string;
  dechargeRemiseNumero: string; dateRemise: string; statut: string;
  montantRestitueMinor: number; montantRetenuMinor: number; motifRetenue?: string;
  dechargeRestitutionNumero?: string; dateRestitution?: string;
  customer?: { nom: string; prenom?: string; raisonSociale?: string; telephone?: string };
  contract?: { _id: string; numero: string };
}

/** La caution n'est pas un montant fixe, et chaque caution genere sa decharge. */
/**
 * La forme d'une caution est une cle technique ('especes', 'cheque'…) : elle
 * ne doit jamais changer, la base et les documents imprimes en dependent.
 * On l'habille ici — Sabrina lit « Espèces », pas « especes ».
 */
const FORME = (): Record<string, string> => T.cautions.formes;

export default function Cautions() {
  const { peut } = useAuth();
  const L = T.cautions;
  const [liste, setListe] = useState<Caution[] | null>(null);
  const [filtre, setFiltre] = useState('detenue');
  const [rendre, setRendre] = useState<Caution | null>(null);
  const [retenu, setRetenu] = useState('0');
  const [motif, setMotif] = useState('');
  const [sigC, setSigC] = useState('');
  const [sigA, setSigA] = useState('');
  const [erreur, setErreur] = useState('');
  const [msg, setMsg] = useState('');

  const charger = useCallback(() => {
    api.get<Caution[]>(`/cautions${filtre ? `?statut=${filtre}` : ''}`).then(setListe);
  }, [filtre]);
  useEffect(charger, [charger]);

  if (!liste) return <Squelette lignes={6} />;
  const lien = (c: Caution, sens: 'remise' | 'restitution') => urlImpression(`/cautions/${c._id}/imprimer/${sens}`, getToken());

  async function restituer() {
    if (!rendre) return;
    setErreur('');
    try {
      await api.post(`/cautions/${rendre._id}/restituer`, {
        montantRetenuMinor: centimes(Number(retenu || 0)),
        motifRetenue: motif, signatureClient: sigC, signatureAgent: sigA,
      });
      setMsg(L.traitee);
      setRendre(null); setRetenu('0'); setMotif(''); setSigC(''); setSigA('');
      charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : L.erreur); }
  }

  const detenues = liste.filter((c) => c.statut === 'detenue');
  const total = detenues.reduce((a, c) => a + c.montantMinor, 0);

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{L.titre}</h1>
          <p>{L.sousTitre}</p>
        </div>
        <div className="actions">
          <select className="btn" value={filtre} onChange={(e) => setFiltre(e.target.value)}>
            <option value="detenue">{L.filtres.detenue}</option>
            <option value="restituee">{L.filtres.restituee}</option>
            <option value="retenue_partielle">{L.filtres.retenue_partielle}</option>
            <option value="saisie">{L.filtres.saisie}</option>
            <option value="">{L.filtres.toutes}</option>
          </select>
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}
      {filtre === 'detenue' && detenues.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="accent" titre={L.detenusTitre(montant(total))}>
            {L.detenusTexte}
          </Bandeau>
        </div>
      )}

      <Carte pad={false}>
        {liste.length === 0 ? <div className="carte-p"><Vide titre={L.vide} /></div> : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                <tr><th>{L.colDecharge}</th><th>{L.colClient}</th><th>{L.colContrat}</th><th>{L.colForme}</th>
                    <th className="droite">{L.colMontant}</th><th className="droite">{L.colRetenu}</th><th>{L.colStatut}</th><th /></tr>
              </thead>
              <tbody>
                {liste.map((c) => (
                  <tr key={c._id}>
                    <td><b>{c.dechargeRemiseNumero}</b><div className="mini sourdine">{dateFr(c.dateRemise)}</div></td>
                    <td>{nomClient(c.customer)}</td>
                    <td className="mini">{c.contract?.numero ?? '—'}</td>
                    <td className="mini sourdine">{FORME()[c.forme] ?? c.forme}{c.numeroPiece && ` · ${c.numeroPiece}`}</td>
                    <td className="droite montant">{montant(c.montantMinor, c.devise)}</td>
                    <td className="droite montant">{c.montantRetenuMinor > 0 ? montant(c.montantRetenuMinor, c.devise) : '—'}
                      {c.motifRetenue && <div className="mini sourdine" style={{ fontWeight: 400 }}>{c.motifRetenue}</div>}</td>
                    <td><Badge statut={c.statut === 'detenue' ? 'confirmee' : c.statut === 'restituee' ? 'en_cours' : 'retard'} /></td>
                    <td className="droite" style={{ whiteSpace: 'nowrap' }}>
                      <a className="btn btn-s" target="_blank" rel="noreferrer" href={lien(c, 'remise')}>{L.decharge}</a>
                      {c.dechargeRestitutionNumero && (
                        <a className="btn btn-s" style={{ marginLeft: 4 }} target="_blank" rel="noreferrer" href={lien(c, 'restitution')}>{L.restitution}</a>
                      )}
                      {c.statut === 'detenue' && peut('modifier.finance') && (
                        <button className="btn btn-s btn-p" style={{ marginLeft: 4 }} onClick={() => { setRendre(c); setRetenu('0'); }}>{L.restituer}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      {!peut('modifier.finance') && (
        <p className="mini sourdine" style={{ marginTop: 12 }}>
          {L.reserveHichem}
        </p>
      )}

      {rendre && (
        <Modale titre={L.restituerTitre(rendre.dechargeRemiseNumero)} onFermer={() => setRendre(null)}>
          <Bandeau type="info" titre={L.detenus(montant(rendre.montantMinor, rendre.devise))}>
            {nomClient(rendre.customer)} · {L.contrat(rendre.contract?.numero)}
          </Bandeau>
          <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
            <Champ label={L.montantRetenu} aide={L.montantRetenuAide}>
              <input type="number" min={0} value={retenu} onChange={(e) => setRetenu(e.target.value)} />
            </Champ>
            {Number(retenu) > 0 && (
              <Champ label={L.motif}>
                <textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder={L.motifPlaceholder} />
              </Champ>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: '1px solid var(--bord)' }}>
              <b>{L.aRestituer}</b>
              <b className="montant" style={{ fontSize: 20 }}>
                {montant(rendre.montantMinor - centimes(Number(retenu || 0)), rendre.devise)}
              </b>
            </div>
            <Signature label={L.sigClient} valeur={sigC} onChange={setSigC} />
            <Signature label={L.sigDirecteur} valeur={sigA} onChange={setSigA} />
          </div>
          {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setRendre(null)}>{L.annuler}</button>
            <button className="btn btn-p" disabled={Number(retenu) > 0 && motif.trim().length < 3} onClick={() => void restituer()}>
              {L.restituerEditer}

            </button>
          </div>
        </Modale>
      )}
    </>
  );
}
